import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";

const KEY = "taskratel:cookie-consent";

export type ConsentValue = "all" | "essential";

export function getCookieConsent(): ConsentValue | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === "all" || v === "essential" ? v : null;
  } catch {
    return null;
  }
}

/** GDPR/NDPR cookie consent banner — mobile responsive, shown once per browser. */
export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(!getCookieConsent()), 800);
    return () => clearTimeout(t);
  }, []);

  const choose = (v: ConsentValue) => {
    try {
      localStorage.setItem(KEY, v);
    } catch {
      /* storage unavailable */
    }
    window.dispatchEvent(new CustomEvent("taskratel:consent", { detail: v }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-2 bottom-2 z-[60] animate-in fade-in slide-in-from-bottom-4 md:inset-x-auto md:bottom-6 md:left-6 md:max-w-md"
    >
      <div className="rounded-xl border border-border bg-card p-4 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/10 p-2 text-primary">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-semibold">We value your privacy</h4>
            <p className="mt-1 text-xs text-muted-foreground">
              We use essential cookies to run TaskRatel, and optional cookies to measure usage and improve
              your experience. Read our{" "}
              <Link to="/cookies" className="underline">cookie policy</Link> and{" "}
              <Link to="/privacy" className="underline">privacy policy</Link>.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => choose("all")}>Accept all</Button>
              <Button size="sm" variant="outline" onClick={() => choose("essential")}>
                Essential only
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}